import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, getApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function NewListing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({
    title: "",
    listing_type: "home_sale",
    address: "", city: "", state: "", zip_code: "",
    price: "", acreage: "", bedrooms: "", bathrooms: "", sqft: "",
    description: "",
    contact_email: user?.email || "",
    contact_phone: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        price: form.price ? parseFloat(form.price) : null,
        acreage: form.acreage ? parseFloat(form.acreage) : null,
        bedrooms: form.bedrooms ? parseInt(form.bedrooms) : null,
        bathrooms: form.bathrooms ? parseFloat(form.bathrooms) : null,
        sqft: form.sqft ? parseInt(form.sqft) : null,
      };
      await api.post("/listings", payload);
      toast.success("Listing posted");
      navigate("/listings");
    } catch (err) {
      toast.error(getApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper" data-testid="new-listing-page">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 py-12">
        <Card className="border-black/5">
          <CardHeader>
            <CardTitle className="font-serif text-3xl">Post a listing</CardTitle>
            <p className="text-sm text-[color:var(--text-secondary)] mt-1">Visible to all Guild visitors. Editable from your portal.</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label>Listing type</Label>
                <Select value={form.listing_type} onValueChange={(v) => set("listing_type", v)}>
                  <SelectTrigger data-testid="listing-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="home_sale">Home for sale</SelectItem>
                    <SelectItem value="home_wanted">Home wanted</SelectItem>
                    <SelectItem value="land_sale">Land for sale</SelectItem>
                    <SelectItem value="land_wanted">Land wanted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Title *</Label>
                <Input required value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="listing-title" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Address *</Label><Input required value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="listing-address" /></div>
                <div><Label>City *</Label><Input required value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="listing-city" /></div>
                <div><Label>State *</Label><Input required value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="listing-state" /></div>
                <div><Label>ZIP *</Label><Input required value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)} data-testid="listing-zip" /></div>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div><Label>Price (USD)</Label><Input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="listing-price" /></div>
                <div><Label>Acreage</Label><Input type="number" value={form.acreage} onChange={(e) => set("acreage", e.target.value)} data-testid="listing-acreage" /></div>
                <div><Label>Sqft</Label><Input type="number" value={form.sqft} onChange={(e) => set("sqft", e.target.value)} data-testid="listing-sqft" /></div>
                <div><Label>Bedrooms</Label><Input type="number" value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} data-testid="listing-bedrooms" /></div>
                <div><Label>Bathrooms</Label><Input type="number" step="0.5" value={form.bathrooms} onChange={(e) => set("bathrooms", e.target.value)} data-testid="listing-bathrooms" /></div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="listing-description" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Contact email *</Label><Input type="email" required value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} data-testid="listing-contact-email" /></div>
                <div><Label>Contact phone</Label><Input value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} data-testid="listing-contact-phone" /></div>
              </div>
              <Button type="submit" disabled={busy} className="w-full bg-[color:var(--bright-green)] hover:bg-[color:var(--bright-green-hover)] text-white" data-testid="listing-submit-btn">
                {busy ? "Posting…" : "Post listing"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
}
